import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {DtoDetail} from 'features/core/shared/dto/dto.detail';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';

/**
 * navItem "Vertrag" of the Provisionssatz OBC — the two rows the Satz hangs off, one under the
 * other: the insurance contract (`cm.Contract`, alias `cp.contractAgent.contract`) and the
 * Provisionszeile (`cp.ProvisionContract`, alias
 * `cp.contractAgent.provisionContract`).
 *
 * WHY BOTH, AND WHY HERE: `cp.ProvisionContractAgent` denormalises `contract` next to its
 * `provisionContract` FK, so a caseworker reading a Satz has two different questions — "which
 * contract is this?" (cm) and "which event time slice of that contract's commission am I in?"
 * (cp). Answering only one of them would force a navigation out of the workbench.
 *
 * WHY THE IDS ARE RESOLVED HERE rather than bound from the OBC's `dto`: `ObjectBaseModeComponent`
 * types `dto` as `DtoDetail`, which declares `id`/`name` and nothing else, so reading
 * `dto.contractDtoId` in a template is rejected under `strictTemplates`. One light read of the
 * Satz keeps the typing honest and costs a single request per pane activation.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * FD-7(a) / FD-8 — the parent-side deferred fields are rendered here as well
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * The Provisionszeile block carries the same pending duty as the calculation pane of the
 * Provisionszeile OBC ({@link CpProvisionContractCalculationComponent}) and the same field set,
 * because the duty is on the FIELD — it has to hold wherever the field is shown. The nullability
 * table that DEFINES that set is documented once, on that class; this component follows it.
 *
 * Five nullable fields render "nicht berechnet" while they are null — with the block-level
 * explanation once above them, see {@link CpProvisionObjectDataComponent} (BT-12):
 * `provisionBase`, `appliedBaseCalculationMode`, `baseReferenceAmountMonthly`,
 * `baseAmountMonthly`, `baseAmountTotal`. `eligibility` is NOT among them — it is
 * `@NotNull @Column(nullable = false)` (`ProvisionContract.java:313-315`) and can never be empty,
 * so a fallback on it would be dead code.
 *
 * WHY `agentsResolved` NEEDS NO EXISTENCE PROBE HERE (BT-5): the discriminator for treating
 * `false` as pending is "does at least one `cp.ProvisionContractAgent` hang off this contract?".
 * In THIS component the answer is yes BY CONSTRUCTION — the pane only ever renders inside an open
 * Provisionssatz, and `provisionContractId` is read off that very Satz, so the Satz being
 * displayed IS such a child row. The probe that
 * {@link CpProvisionContractCalculationComponent} has to make from the parent side is therefore
 * already answered here, and making it would be a redundant request.
 */
@Component({
    selector: 'cp-provision-contract-agent-contract',
    templateUrl: './cp-provision-contract-agent-contract.component.html',
    styleUrls: ['./cp-provision-contract-agent-contract.component.scss'],
    standalone: false
})
export class CpProvisionContractAgentContractComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionContractAgentId: number;

    /** Fields per row — the side view passes 1. */
    @Input() columns: number = 2;

    /** `cm.Contract` behind the Satz (denormalised on the Satz itself). */
    contractId: number;

    /** `cp.ProvisionContract` — the event time slice the Satz belongs to. */
    provisionContractId: number;

    /** True when the Satz itself could not be read — rendered as an error, never as an empty pane. */
    loadFailed: boolean;

    protected dataSubscription: Subscription;

    constructor(protected coreService: MvsCoreService) {
    }

    ngOnInit(): void {
        this.refreshComponent();
    }

    /** Reads the two FK ids off the Provisionssatz. */
    refreshComponent(): void {

        this.loadFailed = false;

        if (!this.provisionContractAgentId) {
            this.contractId = null;
            this.provisionContractId = null;
            this.initialized = true;
            return;
        }

        const crudService: MvsCrudService = this.coreService.getCrudService('cp.ProvisionContractAgent');

        if (!crudService) {
            this.loadFailed = true;
            this.initialized = true;
            return;
        }

        this.busy = true;
        this.dataSubscription?.unsubscribe();

        // light read: no form, no resolved entities - only the two FK ids are needed here.
        this.dataSubscription = crudService.getLight(this.provisionContractAgentId).subscribe({
            next: (dto: DtoDetail) => {
                this.contractId = dto ? dto['contractDtoId'] : null;
                this.provisionContractId = dto ? dto['provisionContractDtoId'] : null;
                this.busy = false;
                this.initialized = true;
            },
            error: () => {
                this.contractId = null;
                this.provisionContractId = null;
                this.loadFailed = true;
                this.busy = false;
                this.initialized = true;
            }
        });
    }

    /** Contract overview: identity, product and period — deliberately no cm detail block. */
    readonly contractFields: string[] = [
        'contractIdentifier',
        'alias',
        'contractTypeDtoName',
        'productDtoName',
        'startDate',
        'endDate',
        'entityStatus'
    ];

    /** Provisionszeile overview: the event time slice plus its calculation result. */
    readonly provisionContractFields: string[] = [
        'provisionTypeDtoName',
        'provisionTypeContractEventDtoName',
        'startDate',
        'endDate',
        'applicationDate',
        'eligibility',
        'eligibilityOverwrite',
        'provisionBaseDtoName',
        'appliedBaseCalculationMode',
        'baseReferenceAmountMonthly',
        'baseAmountMonthly',
        'baseAmountTotal',
        'agentsResolved'
    ];

    /**
     * The five NULLABLE calculation fields of the parent — the set is derived and documented on
     * {@link CpProvisionContractCalculationComponent}; keep the two in step.
     */
    readonly provisionContractPendingFields: string[] = [
        'provisionBaseDtoName',
        'appliedBaseCalculationMode',
        'baseReferenceAmountMonthly',
        'baseAmountMonthly',
        'baseAmountTotal'
    ];

    /**
     * `agentsResolved` — unconditional here, because the child row that makes `false` mean
     * "not stamped yet" rather than "the run found nothing" is the Provisionssatz this pane is
     * rendered inside. See the class javadoc.
     */
    readonly provisionContractPendingFalseFields: string[] = [
        'agentsResolved'
    ];

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionContractAgentId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.dataSubscription?.unsubscribe();
    }

}
